import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

export interface SasbCompletionData {
    orderId: number;
    title?: string;
}

export interface SasbCompletionResult {
    sasbType: 'required' | 'not-required';
    sasbApproved?: boolean;
    scriptIssued?: boolean;
    medicalDirectorStored?: boolean;
    action: 'complete' | 'pause';
}

@Component({
    selector: 'app-dialog-sasb-completion',
    templateUrl: './dialog-sasb-completion.component.html',
    styleUrls: ['./dialog-sasb-completion.component.scss'],
    standalone: false
})
export class DialogSasbCompletionComponent implements OnInit {
    sasbForm: UntypedFormGroup;
    showCheckboxes = false;
    buttonText = 'Pause Appointment';
    allCheckboxesChecked = false;

    constructor(
        private fb: UntypedFormBuilder,
        public dialogRef: MatDialogRef<DialogSasbCompletionComponent>,
        @Inject(MAT_DIALOG_DATA) public data: SasbCompletionData
    ) { }

    ngOnInit(): void {
        this.initializeForm();
    }

    private initializeForm(): void {
        this.sasbForm = this.fb.group({
            sasbType: ['', Validators.required],
            sasbApproved: [false],
            scriptIssued: [false],
            medicalDirectorStored: [false]
        });

        // Watch for changes in SAS-B type selection
        this.sasbForm.get('sasbType')?.valueChanges.subscribe((value) => {
            this.onSasbTypeChange(value);
        });

        // Watch for changes in checkboxes when SAS-B is required
        this.sasbForm.get('sasbApproved')?.valueChanges.subscribe(() => {
            this.updateButtonState();
        });
        this.sasbForm.get('scriptIssued')?.valueChanges.subscribe(() => {
            this.updateButtonState();
        });
        this.sasbForm.get('medicalDirectorStored')?.valueChanges.subscribe(() => {
            this.updateButtonState();
        });
    }

    private onSasbTypeChange(value: string): void {
        if (value === 'required') {
            this.showCheckboxes = true;
            this.buttonText = 'Pause Appointment';

            // Reset checkboxes
            this.sasbForm.patchValue({
                sasbApproved: false,
                scriptIssued: false,
                medicalDirectorStored: false
            });
        } else if (value === 'not-required') {
            this.showCheckboxes = false;
            this.buttonText = 'Complete';

            // Clear checkbox values
            this.sasbForm.patchValue({
                sasbApproved: false,
                scriptIssued: false,
                medicalDirectorStored: false
            });
        } else {
            this.showCheckboxes = false;
            this.buttonText = 'Pause Appointment';
        }

        this.updateButtonState();
    }

    private updateButtonState(): void {
        const sasbType = this.sasbForm.get('sasbType')?.value;

        if (sasbType === 'not-required') {
            this.buttonText = 'Complete';
            this.allCheckboxesChecked = false;
            return;
        }

        if (sasbType === 'required') {
            const sasbApproved = this.sasbForm.get('sasbApproved')?.value;
            const scriptIssued = this.sasbForm.get('scriptIssued')?.value;
            const medicalDirectorStored = this.sasbForm.get('medicalDirectorStored')?.value;

            this.allCheckboxesChecked = sasbApproved && scriptIssued && medicalDirectorStored;
            this.buttonText = this.allCheckboxesChecked ? 'Complete' : 'Pause Appointment';
        }
    }

    onSubmit(): void {
        if (!this.sasbForm.valid) {
            return;
        }

        const formValue = this.sasbForm.value;
        const sasbType = formValue.sasbType;

        let action: 'complete' | 'pause' = 'pause';

        if (sasbType === 'not-required') {
            action = 'complete';
        } else if (sasbType === 'required' && this.allCheckboxesChecked) {
            action = 'complete';
        }

        const result: SasbCompletionResult = {
            sasbType: sasbType,
            action: action
        };

        if (sasbType === 'required') {
            result.sasbApproved = formValue.sasbApproved;
            result.scriptIssued = formValue.scriptIssued;
            result.medicalDirectorStored = formValue.medicalDirectorStored;
        }

        this.dialogRef.close(result);
    }

    onCancel(): void {
        this.dialogRef.close(null);
    }
}